import { FC, ReactElement } from 'react';
import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';

// Assets
import { ReactComponent as IconPeople } from 'Components/ds/assets/icon-people.svg';
import { ReactComponent as IconShield } from 'Components/ds/assets/icon-shield.svg';
import { ReactComponent as IconChartBars } from 'Components/ds/assets/icon-chart-bars-filled.svg';
import { ReactComponent as IconLock } from 'Components/ds/assets/icon-lock-open.svg';
import { ReactComponent as IconChartTree } from 'Components/ds/assets/icon-chart-tree.svg';
import { ReactComponent as IconCoin } from 'Components/ds/assets/icon-dollar.svg';
import { ReactComponent as Interoperability } from 'Components/ds/assets/icon-interoperability.svg';

// Components
import MapleContainer from 'Components/ds/MapleContainer';
import Typography from 'Components/ds/Typography';
import TypographyComplex from 'Components/ds/TypographyComplex';
import Icon3D from 'Components/ds/Icon3D';
import { Feature, SectionHeader } from 'Pages/PoolsV2Transition';

const Features: FC = (): ReactElement => {
  return (
    <Box sx={{ backgroundColor: 'ds.neutral.50' }} id='features'>
      <MapleContainer maxWidth='xl' sx={{ py: 10 }}>
        <Grid container spacing={5} justifyContent='center'>
          <SectionHeader
            subtitle='New Features'
            title={
              <Typography variant='h2' weight='bold' color='ds.neutral.900'>
                Maple Is Upgrading to Launch{' '}
                <Typography variant='h2' weight='bold' color='ds.primary.500' component='span'>
                  Open Term Loans
                </Typography>
              </Typography>
            }
            text='Maple is committed to continuously improving the Maple protocol by introducing new and improved products that put customers first.'
          />
          <Grid item xs={12} md={12} lg={11}>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={5}>
              <Feature
                title='Scheduled and Prorated Withdrawals'
                icon={<Icon3D icon={<IconPeople />} variant='primary' />}
                content={
                  <Stack direction='column' spacing={2}>
                    <TypographyComplex
                      align='left'
                      color='ds.neutral.400'
                      variant='paragraphMedium'
                      globalWeight='regular'
                      items={[
                        {
                          copy: 'All withdrawals now occur during scheduled windows,',
                          type: 'copy',
                          localWeight: 'bold',
                          localColor: 'ds.neutral.600',
                        },
                        {
                          copy: 'enabling lenders to withdraw their funds without having to monitor the loan book for repayments. Withdrawal windows will be available every 7 to 14 days.',
                          type: 'copy',
                        },
                      ]}
                    />
                    <Typography color='ds.neutral.400'>
                      We are instituting prorated withdrawals to accommodate a scenario where pool liquidity is
                      insufficient to cover 100% of withdrawal requests. This ensures that users will be treated
                      equitably and with consideration to their time.
                    </Typography>
                  </Stack>
                }
              />
              <Feature
                title='Pool Delegate Provides First Loss Capital'
                icon={<Icon3D icon={<IconShield width={24} height={24} />} variant='secondary' intensity={300} />}
                content={
                  <TypographyComplex
                    align='left'
                    color='ds.neutral.400'
                    variant='paragraphMedium'
                    globalWeight='regular'
                    items={[
                      {
                        copy: 'First Loss Capital to be provided solely by the Delegate.',
                        type: 'copy',
                        localWeight: 'bold',
                        localColor: 'ds.neutral.600',
                      },
                      {
                        copy: 'First Loss Capital will be provided solely by the Pool Delegate and denominated in the pool asset only. This directly aligns incentives between Lenders and Delegates. MPL is removed from First Loss Capital, disconnecting pool losses from the liquidation of the token.',
                        type: 'copy',
                      },
                    ]}
                  />
                }
              />
            </Stack>
          </Grid>
          <Grid item xs={12} md={12} lg={11}>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={5}>
              <Feature
                title='Automatic Compounding Interest'
                icon={<Icon3D icon={<IconChartBars />} variant='secondary' intensity={300} />}
                content={
                  <TypographyComplex
                    align='left'
                    color='ds.neutral.400'
                    variant='paragraphMedium'
                    globalWeight='regular'
                    items={[
                      {
                        copy: 'All interest earned automatically earns interest.',
                        type: 'copy',
                        localWeight: 'bold',
                        localColor: 'ds.neutral.600',
                      },
                      {
                        copy: 'Interest earned will automatically be reinvested into the pool, providing Lenders with compounding interest on their invested capital.',
                        type: 'copy',
                      },
                    ]}
                  />
                }
              />
              <Feature
                title='Removal of the Lock-Up Period'
                icon={<Icon3D icon={<IconLock />} variant='primary' />}
                content={
                  <TypographyComplex
                    align='left'
                    color='ds.neutral.400'
                    variant='paragraphMedium'
                    globalWeight='regular'
                    items={[
                      {
                        copy: 'Initial lock-up periods have been removed.',
                        type: 'copy',
                        localWeight: 'bold',
                        localColor: 'ds.neutral.600',
                      },
                      {
                        copy: 'Lenders will soon be able to deposit and request withdrawals at convenience, and will not have to wait for the existing 30 day capital lock-up to lapse.',
                        type: 'copy',
                      },
                    ]}
                  />
                }
              />
            </Stack>
          </Grid>
          <Grid item xs={12} md={12} lg={11}>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={5}>
              <Feature
                title='New and Improved Pool Dashboards'
                icon={<Icon3D icon={<IconChartTree />} variant='primary' />}
                content={
                  <TypographyComplex
                    align='left'
                    color='ds.neutral.400'
                    variant='paragraphMedium'
                    globalWeight='regular'
                    items={[
                      {
                        copy: 'At launch, users of Maple 2.0 will get access to all the on-chain loan details.',
                        type: 'copy',
                        localWeight: 'bold',
                        localColor: 'ds.neutral.600',
                      },
                      {
                        copy: 'We are hard at work creating more views into loan payment dates, and refinance history and charts reflecting historical data.',
                        type: 'copy',
                      },
                    ]}
                  />
                }
              />
              <Feature
                title='DeFi Compatible LP Token'
                icon={<Icon3D icon={<IconCoin />} variant='secondary' intensity={300} />}
                content={
                  <Stack direction='column' spacing={5}>
                    <TypographyComplex
                      align='left'
                      color='ds.neutral.400'
                      variant='paragraphMedium'
                      globalWeight='regular'
                      items={[
                        {
                          copy: 'Maple LP Tokens are adopting the ERC-4626 standard,',
                          type: 'copy',
                          localWeight: 'bold',
                          localColor: 'ds.neutral.600',
                        },
                        {
                          copy: 'unlocking interoperability with other DeFi protocols.',
                          type: 'copy',
                        },
                      ]}
                    />
                    <Box sx={{ textAlign: 'center' }}>
                      <Interoperability />
                    </Box>
                  </Stack>
                }
              />
            </Stack>
          </Grid>
        </Grid>
      </MapleContainer>
    </Box>
  );
};

export default Features;
